import {
  Button,
  Flex,
  InputNumber,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
} from 'antd';
import { IconHistory, IconDeviceFloppy } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { DisplayPlatformSetting, SettingValue } from '../../types';
import {
  deriveSettingOptionTranslationKey,
  deriveSettingTranslationKey,
  getNumericSettingConstraints,
  getSettingEnumOptions,
  validateCronExpression,
} from '../../utils';
import { CronSettingInput } from './components/CronSettingInput/CronSettingInput';
import { SettingMetadata } from './components/SettingMetadata/SettingMetadata';
import { useTimezoneOptions } from './hooks/useTimezoneOptions';

const { Paragraph, Text, Title } = Typography;

export interface SettingRowProps {
  setting: DisplayPlatformSetting;
  draftValue?: SettingValue;
  isDirty: boolean;
  onChange: (input: unknown) => void;
  onApply: () => void;
  onHistory: () => void;
}

export function SettingRow({
  setting,
  draftValue,
  isDirty,
  onChange,
  onApply,
  onHistory,
}: SettingRowProps) {
  const { t } = useTranslation('settings');
  const value = isDirty ? (draftValue as SettingValue) : setting.value;
  const { min, max, step } = getNumericSettingConstraints(setting.constraints);
  const timezoneOptions = useTimezoneOptions(String(value));
  const isCronValid =
    setting.valueType !== 'CRON' || validateCronExpression(String(value));
  const unit = setting.unit
    ? t(`units.${setting.unit}`, { defaultValue: setting.unit })
    : t('metadata.none');

  return (
    <Flex vertical gap={16} className="platform-setting-row">
      <Flex justify="space-between" align="flex-start" gap={20} wrap>
        <Flex vertical gap={4} className="platform-setting-copy">
          <Space size={8} wrap>
            <Title level={5}>
              {t(deriveSettingTranslationKey(setting.key, 'label'), {
                defaultValue: setting.key,
              })}
            </Title>
            {isDirty ? <Tag color="gold">{t('states.unsaved')}</Tag> : null}
            <Tag>
              {t(`classifications.${setting.classification}`, {
                defaultValue: setting.classification,
              })}
            </Tag>
          </Space>
          <Paragraph type="secondary">
            {t(deriveSettingTranslationKey(setting.key, 'description'), {
              defaultValue: t('metadata.noDescription'),
            })}
          </Paragraph>
          <Text code>{setting.key}</Text>
        </Flex>

        <Flex gap={8}>
          <Button icon={<IconHistory size={16} />} onClick={onHistory}>
            {t('actions.history')}
          </Button>
          <Button
            type="primary"
            icon={<IconDeviceFloppy size={16} />}
            disabled={!isDirty || !setting.editable || !isCronValid}
            onClick={onApply}
          >
            {t('actions.reviewApply')}
          </Button>
        </Flex>
      </Flex>

      <div
        className={
          setting.valueType === 'CRON'
            ? 'platform-setting-control platform-setting-control--wide'
            : 'platform-setting-control'
        }
      >
        {setting.valueType === 'BOOLEAN' ? (
          <Switch
            checked={Boolean(value)}
            disabled={!setting.editable}
            checkedChildren={t('values.enabled')}
            unCheckedChildren={t('values.disabled')}
            onChange={onChange}
          />
        ) : null}
        {setting.valueType === 'NUMBER' ||
        setting.valueType === 'DURATION_MS' ? (
          <InputNumber
            value={Number(value)}
            min={min}
            max={max}
            step={step}
            disabled={!setting.editable}
            addonAfter={unit}
            onChange={(nextValue) => {
              if (nextValue !== null) onChange(nextValue);
            }}
          />
        ) : null}
        {setting.valueType === 'CRON' ? (
          <CronSettingInput
            value={String(value)}
            disabled={!setting.editable}
            onChange={onChange}
          />
        ) : null}
        {setting.valueType === 'ENUM' ? (
          <Select
            value={String(value)}
            options={getSettingEnumOptions(setting.options).map((option) => ({
              value: option,
              label: t(deriveSettingOptionTranslationKey(setting.key, option), {
                defaultValue: option,
              }),
            }))}
            disabled={!setting.editable}
            onChange={onChange}
          />
        ) : null}
        {setting.valueType === 'TIMEZONE' ? (
          <Select
            showSearch
            value={String(value)}
            options={timezoneOptions}
            disabled={!setting.editable}
            onChange={onChange}
          />
        ) : null}
      </div>

      <SettingMetadata setting={setting} />
    </Flex>
  );
}
